import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../../services/api';
import { usePipelineCostItems } from '../../credits/pipelineCost';
import { stripOutputContract } from './shared';
import { ChoiceCard, FlowCanvas, FlowHeader, InspectorHeader, InstructionTab, ToggleCard, type FlowEdge, type FlowNode, type FlowSource } from './FlowCanvas';
import { MergeTimeline, type MergeSegment } from './MergeTimeline';

export type VideoStepKind = 'image' | 'avatar_look' | 'runway' | 'avatar_chromakey' | 'avatar_fullscreen' | 'merge' | 'seo';
export type AvatarEngine = 'avatar-iii' | 'avatar-iv';
export interface VideoPipelineStep {
  id: string;
  title: string;
  kind: VideoStepKind;
  references?: string[];
  useBrand: boolean;
  overlay?: boolean;
  lookSource?: 'existing' | 'pipeline';
  lookId?: string;
  // Motor HeyGen da fala do avatar. Ausente = Avatar III (padrão dos pipelines antigos).
  avatarEngine?: AvatarEngine;
  segments?: MergeSegment[];
  promptInstruction: string;
  status?: string;
  imageUrl?: string;
  url?: string;
}
export interface VideoPipeline { id: string; name: string; description?: string; isDefault?: boolean; steps: VideoPipelineStep[] }

export const VIDEO_KIND_META: Record<VideoStepKind, { icon: string; label: string; short: string; desc: string; cost: string; head: string; ring: string }> = {
  image: { icon: '🖼️', label: 'Imagem da cena', short: 'Cena', desc: 'Gera a imagem que vira o primeiro quadro de um clipe.', cost: 'incluso no preço da cena', head: 'bg-indigo-600', ring: 'ring-indigo-300' },
  avatar_look: { icon: '👗', label: 'Novo look do avatar', short: 'Look', desc: 'Cria o seu avatar com outra roupa ou em outro cenário.', cost: '1 geração de avatar', head: 'bg-fuchsia-600', ring: 'ring-fuchsia-300' },
  runway: { icon: '🎬', label: 'Clipe animado (5s)', short: 'Clipe', desc: 'Anima uma imagem e gera um clipe de 5 segundos (Veo).', cost: '1 cena de vídeo', head: 'bg-sky-600', ring: 'ring-sky-300' },
  avatar_chromakey: { icon: '🎙️', label: 'Avatar reagindo', short: 'Avatar sobreposto', desc: 'O avatar fala com o fundo removido, por cima dos clipes.', cost: '1 geração de avatar', head: 'bg-emerald-600', ring: 'ring-emerald-300' },
  avatar_fullscreen: { icon: '🧑', label: 'Avatar em tela cheia', short: 'Avatar', desc: 'O avatar fala direto para a câmera, ocupando a tela.', cost: '1 geração de avatar', head: 'bg-teal-600', ring: 'ring-teal-300' },
  merge: { icon: '🎞️', label: 'Vídeo final', short: 'Vídeo final', desc: 'Junta clipes e avatar, com gancho e legendas.', cost: 'sem custo', head: 'bg-violet-600', ring: 'ring-violet-300' },
  seo: { icon: '🔎', label: 'SEO', short: 'SEO', desc: 'Títulos e nomes de arquivo otimizados para os vídeos.', cost: 'sem custo', head: 'bg-amber-500', ring: 'ring-amber-300' },
};
const REF_KINDS: VideoStepKind[] = ['image', 'avatar_look', 'runway'];
const IMAGE_KINDS: VideoStepKind[] = ['image', 'avatar_look'];
const CLIP_KINDS: VideoStepKind[] = ['runway', 'avatar_chromakey', 'avatar_fullscreen'];
const ASSET_KINDS: VideoStepKind[] = ['image', 'avatar_look', 'runway', 'avatar_chromakey', 'avatar_fullscreen', 'merge'];
const AVATAR_SPEECH_KINDS: VideoStepKind[] = ['avatar_chromakey', 'avatar_fullscreen'];
type AvatarEngineOption = { id: AvatarEngine; label: string; creditsPerSecond: number | null; missing: boolean };
const fmtCredits = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 4 });

export const videoStepChip = (s: VideoPipelineStep) => ({ icon: VIDEO_KIND_META[s.kind]?.icon || '🎬', label: s.title });

function uid() { return Math.random().toString(36).slice(2, 10); }
const refsOf = (s: VideoPipelineStep) => (s.references && s.references.length ? s.references : ['prev']);

const SRC_PRODUCT = 'src:product';
const SRC_AVATAR = 'src:avatar';
const SRC_REFS = 'src:refs';
const C_PRODUCT = '#64748b';
const C_STEP = '#4f46e5';
const C_AVATAR = '#ea580c';
const C_REFS = '#c026d3';
const C_MERGE = '#7c3aed';

// Resolve um token de referência para o node de onde a imagem sai (espelha
// _resolveRefToken/_buildCtx do backend: prev cai para a 1ª imagem e depois para a foto).
function tokenSource(token: string, idx: number, steps: VideoPipelineStep[]): string | null {
  const before = steps.slice(0, idx);
  const firstImage = steps.find(s => IMAGE_KINDS.includes(s.kind));
  const firstFallback = firstImage && steps.indexOf(firstImage) < idx ? firstImage.id : SRC_PRODUCT;
  if (token === 'original' || token.startsWith('product:')) return SRC_PRODUCT;
  if (token === 'first') return firstFallback;
  if (token === 'prev') {
    const prev = [...before].reverse().find(s => IMAGE_KINDS.includes(s.kind));
    return prev ? prev.id : firstFallback;
  }
  if (token === 'look') return [...before].reverse().find(s => s.kind === 'avatar_look')?.id || null;
  if (token.startsWith('ref:')) return SRC_REFS;
  if (token.startsWith('avatarlook:')) return SRC_AVATAR;
  return null;
}

export function VideoPipelineFlow({ pipeline, avatarLooks, onSaved, onCloned, onDeleted, onDirtyChange }: {
  pipeline: VideoPipeline;
  avatarLooks: { id: string; name: string }[];
  onSaved: (p: VideoPipeline) => void;
  onCloned: (p: VideoPipeline) => void;
  onDeleted: (id: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [draft, setDraft] = useState<VideoPipeline>(pipeline);
  const [selectedId, setSelectedId] = useState<string | null>(pipeline.steps[0]?.id || null);
  const [tab, setTab] = useState<'basico' | 'entradas' | 'instrucao'>('basico');
  const [saving, setSaving] = useState(false);
  const [avatarEngines, setAvatarEngines] = useState<AvatarEngineOption[] | null>(null);

  // Créditos por segundo de cada motor vêm da tabela de custos do super admin (nunca fixos na tela).
  useEffect(() => {
    let alive = true;
    api.listEcommerceAvatarEngines()
      .then(r => { if (alive) setAvatarEngines(r?.success ? r.engines : []); })
      .catch(() => { if (alive) setAvatarEngines([]); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    setDraft(pipeline);
    setSelectedId(pipeline.steps[0]?.id || null);
    setTab('basico');
  }, [pipeline.id]);

  const readonly = !!draft.isDefault;
  const dirty = JSON.stringify({ n: draft.name, d: draft.description || '', s: draft.steps }) !== JSON.stringify({ n: pipeline.name, d: pipeline.description || '', s: pipeline.steps });
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty]);

  const steps = draft.steps;
  const costItems = usePipelineCostItems('video', steps);
  const selIdx = steps.findIndex(s => s.id === selectedId);
  const selected = selIdx >= 0 ? steps[selIdx] : null;
  const approvalId = steps.find(s => ASSET_KINDS.includes(s.kind))?.id;
  const mergeIdx = steps.findIndex(s => s.kind === 'merge');

  // ── Mutação das etapas ─────────────────────────────────────────────────────
  const patchStep = (idx: number, patch: Partial<VideoPipelineStep>) =>
    setDraft(d => ({ ...d, steps: d.steps.map((s, i) => i === idx ? { ...s, ...patch } : s) }));

  // Etapas que usam "Asset anterior" dependem da ordem.
  const usesPrev = (s?: VideoPipelineStep) => !!s && REF_KINDS.includes(s.kind) && refsOf(s).includes('prev');
  const prevRefGuard = (affected: (VideoPipelineStep | undefined)[], acao: string) => {
    const culprit = affected.find(usesPrev);
    if (!culprit) return false;
    toast.error(`"${culprit.title}" usa a imagem da etapa anterior. Antes de ${acao}, troque essa entrada em Entradas.`);
    return true;
  };

  const buildStep = (kind: VideoStepKind): VideoPipelineStep => ({
    id: uid(), kind, title: VIDEO_KIND_META[kind].label,
    references: REF_KINDS.includes(kind) ? ['prev'] : undefined,
    useBrand: false,
    overlay: kind === 'merge',
    lookSource: 'existing',
    ...(AVATAR_SPEECH_KINDS.includes(kind) ? { avatarEngine: 'avatar-iii' as AvatarEngine } : {}),
    promptInstruction:
      kind === 'seo' ? 'Gere títulos SEO e nomes de arquivo otimizados para cada asset.'
      : kind === 'image' ? 'Descreva a cena de vídeo desejada. Preserve fielmente o produto da referência.'
      : kind === 'runway' ? 'Descreva o movimento da cena (imagem para vídeo). Câmera sutil, produto nítido.'
      : kind === 'avatar_look' ? 'Descreva um novo look para o avatar (mesma pessoa, roupa/cenário diferente).'
      : (kind === 'avatar_chromakey' || kind === 'avatar_fullscreen') ? 'Escreva a narração falada pelo avatar em PT-BR, com gancho forte no início e CTA no fim.'
      : '',
  });
  const insertStep = (kind: string, at: number) => {
    if (prevRefGuard([steps[at]], 'adicionar uma etapa aqui')) return;
    const step = buildStep(kind as VideoStepKind);
    setDraft(d => { const next = [...d.steps]; next.splice(at, 0, step); return { ...d, steps: next }; });
    setSelectedId(step.id);
    setTab(kind === 'merge' ? 'basico' : 'instrucao');
  };
  const removeStep = (idx: number) => {
    if (prevRefGuard([steps[idx + 1]], 'remover esta etapa')) return;
    if (!window.confirm(`Remover a etapa "${steps[idx].title}"?`)) return;
    setDraft(d => ({ ...d, steps: d.steps.filter((_, i) => i !== idx) }));
    setSelectedId(steps[idx + 1]?.id || steps[idx - 1]?.id || null);
  };
  const moveStep = (from: number, to: number) => {
    if (to < 0 || to >= steps.length || from === to) return;
    if (prevRefGuard([steps[from], steps[from + 1], steps[to], steps[to + 1]], 'mudar a ordem')) return;
    setDraft(d => { const next = [...d.steps]; const [m] = next.splice(from, 1); next.splice(to, 0, m); return { ...d, steps: next }; });
  };

  // ── Persistência ───────────────────────────────────────────────────────────
  const save = async () => {
    if (!draft.name.trim()) { toast.error('Dê um nome ao estilo.'); return; }
    setSaving(true);
    try {
      const res = await api.updateEcommerceVideoPipeline(draft.id, { name: draft.name, description: draft.description, steps: draft.steps });
      toast.success('Estilo salvo');
      onSaved(res.pipeline);
    } catch (e: any) { toast.error(e.message || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };
  const clone = async () => {
    try { const res = await api.cloneEcommerceVideoPipeline(draft.id); toast.success('Cópia criada — agora é só editar'); onCloned(res.pipeline); }
    catch (e: any) { toast.error(e.message || 'Erro ao duplicar'); }
  };
  const remove = async () => {
    if (!window.confirm(`Excluir o estilo "${draft.name}"?`)) return;
    try { await api.deleteEcommerceVideoPipeline(draft.id); toast.success('Estilo excluído'); onDeleted(draft.id); }
    catch (e: any) { toast.error(e.message || 'Erro ao excluir'); }
  };

  // ── Grafo ──────────────────────────────────────────────────────────────────
  const edges: FlowEdge[] = [];
  const pushEdge = (e: FlowEdge) => { if (!edges.some(x => x.from === e.from && x.to === e.to)) edges.push(e); };
  const styleFor = (from: string): Pick<FlowEdge, 'color' | 'route' | 'dashed'> =>
    from === SRC_PRODUCT ? { color: C_PRODUCT, route: 'above' }
    : from === SRC_AVATAR ? { color: C_AVATAR, route: 'below' }
    : from === SRC_REFS ? { color: C_REFS, route: 'below', dashed: true }
    : { color: C_STEP, route: 'above' };
  steps.forEach((s, idx) => {
    if (REF_KINDS.includes(s.kind)) {
      refsOf(s).forEach(t => { const from = tokenSource(t, idx, steps); if (from && from !== s.id) pushEdge({ from, to: s.id, ...styleFor(from) }); });
    } else if (s.kind === 'avatar_fullscreen') {
      const look = s.lookSource === 'pipeline' ? [...steps.slice(0, idx)].reverse().find(x => x.kind === 'avatar_look')?.id : SRC_AVATAR;
      if (look) pushEdge({ from: look, to: s.id, ...styleFor(look) });
    } else if (s.kind === 'avatar_chromakey') {
      pushEdge({ from: SRC_AVATAR, to: s.id, ...styleFor(SRC_AVATAR) });
    } else if (s.kind === 'merge') {
      steps.slice(0, idx).filter(x => CLIP_KINDS.includes(x.kind)).forEach(x => pushEdge({ from: x.id, to: s.id, color: C_MERGE, route: 'above', onlyWhenHot: true }));
    }
  });
  const usedSources = new Set(edges.map(e => e.from));
  const sources: FlowSource[] = [
    { id: SRC_PRODUCT, icon: '📦', title: 'Foto do produto', subtitle: 'do catálogo ou enviada', borderCls: 'border-slate-300', labelCls: 'text-slate-400' },
    ...(usedSources.has(SRC_AVATAR) ? [{ id: SRC_AVATAR, icon: '👤', title: 'Seu avatar', subtitle: 'looks já cadastrados', borderCls: 'border-orange-300', labelCls: 'text-orange-400' }] : []),
    ...(usedSources.has(SRC_REFS) ? [{ id: SRC_REFS, icon: '🖼️', title: 'Referências', subtitle: 'fotos extras / anexadas', dashed: true, borderCls: 'border-fuchsia-300', labelCls: 'text-fuchsia-400' }] : []),
  ];
  const nodes: FlowNode[] = steps.map(s => {
    const meta = VIDEO_KIND_META[s.kind];
    const snippet = s.kind === 'merge'
      ? `${(s.segments || []).length ? `${(s.segments || []).length} trechos` : 'Clipes em sequência'}${s.overlay !== false ? ' · gancho + legendas' : ''}`
      : stripOutputContract(s.promptInstruction);
    return {
      id: s.id, icon: meta.icon, kindLabel: meta.short, headCls: meta.head, ringCls: meta.ring,
      title: s.title, snippet, approval: s.id === approvalId,
      badges: (s.useBrand || AVATAR_SPEECH_KINDS.includes(s.kind)) ? (
        <>
          {AVATAR_SPEECH_KINDS.includes(s.kind) && <span className="rounded bg-teal-700 px-1.5 py-0.5 text-[9px] font-bold text-white">{s.avatarEngine === 'avatar-iv' ? 'AVATAR IV' : 'AVATAR III'}</span>}
          {s.useBrand && <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[9px] font-bold text-white">BRAND</span>}
        </>
      ) : undefined,
    };
  });

  // ── Opções de entrada (referências) da etapa selecionada ──────────────────
  const refOptionsFor = (idx: number) => {
    const before = steps.slice(0, idx);
    const lastImage = [...before].reverse().find(s => IMAGE_KINDS.includes(s.kind));
    const firstImage = steps.find(s => IMAGE_KINDS.includes(s.kind));
    const lastLook = [...before].reverse().find(s => s.kind === 'avatar_look');
    return [
      { value: 'original', label: '📦 Foto do produto', show: true },
      { value: 'prev', label: lastImage ? `↩ Imagem da etapa anterior ("${lastImage.title}")` : '↩ Imagem da etapa anterior', show: !!lastImage },
      { value: 'first', label: firstImage ? `⭐ Primeira imagem gerada ("${firstImage.title}")` : '⭐ Primeira imagem gerada', show: !!firstImage && steps.indexOf(firstImage) < idx },
      { value: 'look', label: lastLook ? `👗 Look gerado em "${lastLook.title}"` : '👗 Último look gerado', show: !!lastLook },
      { value: 'ref:0', label: '🖼️ Referência #1 (fotos extras / anexadas)', show: true },
      { value: 'ref:1', label: '🖼️ Referência #2', show: true },
      { value: 'ref:2', label: '🖼️ Referência #3', show: true },
      ...avatarLooks.map(l => ({ value: `avatarlook:${l.id}`, label: `👤 Avatar: ${l.name}`, show: true })),
    ];
  };

  const meta = selected ? VIDEO_KIND_META[selected.kind] : null;
  const tabsFor = (s: VideoPipelineStep) => {
    const t: { id: 'basico' | 'entradas' | 'instrucao'; label: string }[] = [{ id: 'basico', label: 'Básico' }];
    if (REF_KINDS.includes(s.kind) || s.kind === 'avatar_fullscreen' || s.kind === 'avatar_chromakey') t.push({ id: 'entradas', label: 'Entradas' });
    if (s.kind !== 'merge') t.push({ id: 'instrucao', label: s.kind.startsWith('avatar_') && s.kind !== 'avatar_look' ? 'Roteiro' : 'Instrução' });
    return t;
  };
  const effectiveTab = selected && tabsFor(selected).some(t => t.id === tab) ? tab : 'basico';

  return (
    <div className="space-y-4">
      <FlowHeader name={draft.name} description={draft.description || ''}
        onName={v => setDraft(d => ({ ...d, name: v }))} onDescription={v => setDraft(d => ({ ...d, description: v }))}
        readonly={readonly} dirty={dirty} saving={saving} costItems={costItems}
        onSave={save} onClone={clone} onRemove={remove} />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-4">
          <FlowCanvas
            sources={sources} nodes={nodes} edges={edges}
            legend={[
              { color: C_PRODUCT, label: 'foto do produto' }, { color: C_STEP, label: 'imagem gerada' },
              { color: C_AVATAR, label: 'avatar' }, { color: C_REFS, label: 'referências', dashed: true },
              { color: C_MERGE, label: 'entra no vídeo final' },
            ]}
            selectedId={selectedId} onSelect={setSelectedId} readonly={readonly} onMove={moveStep}
            insertOptions={(Object.keys(VIDEO_KIND_META) as VideoStepKind[]).map(k => ({ key: k, icon: VIDEO_KIND_META[k].icon, label: VIDEO_KIND_META[k].label, hint: VIDEO_KIND_META[k].desc }))}
            onInsert={insertStep}
          />

          {/* Montagem do vídeo final */}
          {mergeIdx >= 0 && (
            <section className={`rounded-2xl border bg-white p-4 shadow-sm ${selectedId === steps[mergeIdx].id ? 'border-violet-300 ring-2 ring-violet-100' : 'border-slate-200'}`}>
              <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h4 className="text-sm font-bold text-slate-900">🎞️ Montagem do vídeo final</h4>
                  <p className="text-[11px] text-slate-500">Defina em quais trechos aparece o avatar ou um clipe. Arraste para reordenar, puxe as bordas para mudar a duração, clique para editar.</p>
                </div>
                <button type="button" onClick={() => setSelectedId(steps[mergeIdx].id)} className="text-xs font-semibold text-violet-700 hover:underline">Opções do vídeo final</button>
              </div>
              <MergeTimeline
                segments={steps[mergeIdx].segments || []}
                stepsBefore={steps.slice(0, mergeIdx)}
                readonly={readonly}
                onChange={segs => patchStep(mergeIdx, { segments: segs })}
              />
            </section>
          )}
        </div>

        {/* Painel da etapa selecionada */}
        <aside className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm xl:sticky xl:top-4 xl:self-start">
          {!selected || !meta ? (
            <p className="py-10 text-center text-sm text-slate-500">Selecione uma etapa no fluxo para ver os detalhes.</p>
          ) : (
            <div className="space-y-4">
              <InspectorHeader index={selIdx} title={selected.title} headCls={meta.head} readonly={readonly}
                canLeft={selIdx > 0} canRight={selIdx < steps.length - 1}
                onLeft={() => moveStep(selIdx, selIdx - 1)} onRight={() => moveStep(selIdx, selIdx + 1)} onRemove={() => removeStep(selIdx)}
                tabs={tabsFor(selected)} tab={effectiveTab} onTab={setTab} />

              {effectiveTab === 'basico' && (
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Nome da etapa</span>
                    <input value={selected.title} onChange={e => patchStep(selIdx, { title: e.target.value })} disabled={readonly}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50 disabled:text-slate-500" />
                  </label>
                  <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
                    <p><b>{meta.icon} {meta.label}:</b> {meta.desc}</p>
                    <p className="mt-1"><b>Custo:</b> {AVATAR_SPEECH_KINDS.includes(selected.kind) ? `segundos de avatar ${selected.avatarEngine === 'avatar-iv' ? 'IV' : 'III'} + voz` : meta.cost}</p>
                    {selected.id === approvalId && <p className="mt-1 text-amber-700"><b>Pede aprovação:</b> é o primeiro resultado — você aprova antes do resto ser gerado.</p>}
                  </div>
                  {AVATAR_SPEECH_KINDS.includes(selected.kind) && (() => {
                    const current: AvatarEngine = selected.avatarEngine === 'avatar-iv' ? 'avatar-iv' : 'avatar-iii';
                    const rateOf = (id: AvatarEngine) => {
                      const opt = avatarEngines?.find(e => e.id === id);
                      return opt && !opt.missing && opt.creditsPerSecond != null ? opt.creditsPerSecond : null;
                    };
                    const rateText = (id: AvatarEngine) => {
                      if (!avatarEngines) return 'carregando preço…';
                      const rate = rateOf(id);
                      return rate == null ? 'sem tarifa ativa na tabela de custos — não pode ser gerado' : `${fmtCredits(rate)} créditos por segundo de vídeo`;
                    };
                    // Comparação de custo pela tabela vigente (o super admin pode mudar as tarifas).
                    const r3 = rateOf('avatar-iii'), r4 = rateOf('avatar-iv');
                    const ivCostNote = r3 != null && r4 != null
                      ? (r4 > r3 ? `Custa ${fmtCredits(r4 - r3)} créditos a mais por segundo que o Avatar III.` : r4 < r3 ? 'Hoje custa menos por segundo que o Avatar III.' : 'Hoje custa o mesmo por segundo que o Avatar III.')
                      : '';
                    return (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-slate-600">Modelo do avatar (HeyGen)</p>
                        <ChoiceCard active={current === 'avatar-iii'} disabled={readonly}
                          title="HeyGen Avatar III" desc={`Padrão · ${rateText('avatar-iii')}`}
                          onClick={() => patchStep(selIdx, { avatarEngine: 'avatar-iii' })} />
                        <ChoiceCard active={current === 'avatar-iv'} disabled={readonly}
                          title="HeyGen Avatar IV" desc={`Mais qualidade (expressão e lipsync mais naturais) · ${rateText('avatar-iv')}`}
                          onClick={() => patchStep(selIdx, { avatarEngine: 'avatar-iv' })} />
                        {current === 'avatar-iv' && (
                          <p className="rounded-lg bg-amber-50 p-2 text-[11px] text-amber-800">O Avatar IV tem mais qualidade. {ivCostNote} O valor acima é por segundo; a cobrança real usa a duração do vídeo que o HeyGen entregar.</p>
                        )}
                      </div>
                    );
                  })()}
                  {selected.kind === 'merge' && (
                    <ToggleCard checked={selected.overlay !== false} onChange={v => patchStep(selIdx, { overlay: v })} disabled={readonly}
                      title="Gancho e legendas na tela" desc="Coloca o texto de gancho no início e legendas da narração por cima do vídeo." />
                  )}
                  {selected.kind !== 'seo' && (
                    <ToggleCard checked={selected.useBrand} onChange={v => patchStep(selIdx, { useBrand: v })} disabled={readonly}
                      title="Aplicar minha marca" desc="Logo, cores e fontes da Brand entram nesta etapa." />
                  )}
                  {selected.kind === 'merge' && <p className="text-[11px] text-slate-500">A ordem dos trechos fica na faixa <b>Montagem do vídeo final</b>, abaixo do fluxo.</p>}
                  {selected.kind === 'seo' && <p className="text-[11px] text-slate-500">Lê os dados do produto e todos os vídeos/imagens gerados.</p>}
                </div>
              )}

              {effectiveTab === 'entradas' && selected.kind === 'avatar_chromakey' && (
                <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">Usa a versão <b>com fundo removido</b> do seu avatar. Não precisa escolher imagem — o roteiro fica na aba Roteiro.</p>
              )}

              {effectiveTab === 'entradas' && selected.kind === 'avatar_fullscreen' && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-slate-600">Qual visual do avatar aparece?</p>
                  <ChoiceCard active={(selected.lookSource || 'existing') === 'existing'} disabled={readonly}
                    title="Um look já cadastrado" desc="Usa um dos looks prontos do seu avatar."
                    onClick={() => patchStep(selIdx, { lookSource: 'existing' })} />
                  {(selected.lookSource || 'existing') === 'existing' && (
                    <select value={selected.lookId || ''} disabled={readonly} onChange={e => patchStep(selIdx, { lookId: e.target.value || undefined })}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                      <option value="">{avatarLooks.length ? 'Padrão (1º look pronto)' : 'Nenhum look cadastrado'}</option>
                      {!avatarLooks.some(l => l.id === selected.lookId) && selected.lookId && <option value={selected.lookId}>Look selecionado (removido)</option>}
                      {avatarLooks.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  )}
                  <ChoiceCard active={selected.lookSource === 'pipeline'} disabled={readonly}
                    title="O look criado neste estilo" desc={steps.slice(0, selIdx).some(s => s.kind === 'avatar_look') ? 'Usa o último "Novo look do avatar" antes desta etapa.' : 'Adicione uma etapa "Novo look do avatar" antes desta para usar.'}
                    onClick={() => patchStep(selIdx, { lookSource: 'pipeline' })} />
                </div>
              )}

              {effectiveTab === 'entradas' && REF_KINDS.includes(selected.kind) && (() => {
                const refs = refsOf(selected);
                const options = refOptionsFor(selIdx);
                const setRefs = (next: string[]) => patchStep(selIdx, { references: next.length ? next : ['prev'] });
                const missing = (v: string) => !options.some(o => o.value === v && o.show);
                return (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-slate-600">Imagens que entram nesta etapa</p>
                      <p className="text-[11px] text-slate-500">{selected.kind === 'runway' ? 'A imagem 1 vira o primeiro quadro do clipe.' : 'Na instrução, cite como "imagem 1", "imagem 2"… nesta ordem.'}</p>
                    </div>
                    <ol className="space-y-1.5">
                      {refs.map((ref, ri) => (
                        <li key={ri} className="flex items-center gap-2">
                          <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-indigo-600 text-[11px] font-bold text-white">{ri + 1}</span>
                          <select value={ref} disabled={readonly} onChange={e => setRefs(refs.map((r, i) => i === ri ? e.target.value : r))}
                            className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-xs">
                            {missing(ref) && <option value={ref}>{ref.startsWith('avatarlook:') ? '👤 Look do avatar (removido)' : ref === 'look' ? '👗 Último look (nenhum antes desta etapa)' : '↩ Imagem anterior (nenhuma antes desta etapa)'}</option>}
                            {options.filter(o => o.show).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          {!readonly && refs.length > 1 && (
                            <button type="button" onClick={() => setRefs(refs.filter((_, i) => i !== ri))} title="Remover" className="flex-shrink-0 rounded px-1.5 text-rose-500 hover:text-rose-700">✕</button>
                          )}
                        </li>
                      ))}
                    </ol>
                    {!readonly && (
                      <button type="button" onClick={() => setRefs([...refs, 'original'])} className="rounded-lg border border-dashed border-indigo-300 px-2.5 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50">+ Adicionar imagem</button>
                    )}
                    <p className="rounded-lg bg-slate-50 p-2 text-[11px] text-slate-500"><b>Referências #1, #2, #3:</b> produto do catálogo usa as fotos extras do cadastro; no envio manual, as fotos que você anexar.</p>
                  </div>
                );
              })()}

              {effectiveTab === 'instrucao' && (
                <InstructionTab stepKey={selected.id} instruction={selected.promptInstruction} readonly={readonly}
                  onChange={v => patchStep(selIdx, { promptInstruction: v })}
                  runCopilot={async (direction) => (await api.assistEcommerceVideoPrompt(selected.kind, selected.promptInstruction, direction, selected.references, selected.title)).prompt}
                  placeholder={selected.kind.startsWith('avatar_') && selected.kind !== 'avatar_look'
                    ? 'Ex.: começar com uma pergunta, tom animado, mostrar o problema que o produto resolve'
                    : selected.kind === 'runway' ? 'Ex.: câmera girando devagar em volta do produto, luz suave'
                    : 'Ex.: produto em cima da mesa da cozinha, luz natural'}
                  help={selected.kind.startsWith('avatar_') && selected.kind !== 'avatar_look' ? 'É a direção do roteiro que o avatar vai falar.' : 'É o roteiro que a IA segue para montar esta etapa.'} />
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
